import { useCallback, useEffect, useState } from "react";
import "./credit-card-details.scss";
import {
  addAlphaChannel,
  GSForm,
  GSSidePanelPage,
  useFormDataValidation,
} from "golfstatus_react_components";
import {
  faChevronLeft,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";

import { useDispatch, useSelector } from "react-redux";

import { getCurrentUser } from "../../../../app/gs-session";
import {
  getSaveBannerActions,
  useNotificationAction,
  useNotificationBanner,
  useNotifications,
} from "../../../../hooks/notificationHooks";
import {
  getPaymentOption,
  getUserPaymentOptions,
} from "../../../../reducers/userSlice";
import {
  createBillingAddressThunk,
  internationalCardCheckThunk,
  selectRegistrationCustomer,
  selectRegistrationOrder,
  setBillingAddress,
  updateBillingAddressThunk,
  updateOrder,
  updateOrderThunk,
  selectPaymentDetails,
  setPaymentDetails,
  addNotification,
  clearNotifications,
  selectLoading,
} from "../../../../reducers/registrationOrderSlice";

import {
  getBankFormSections,
  getCreditCardFormSections,
} from "../../../../forms/CheckoutForm";

import {
  useElements,
  useStripe,
  CardNumberElement,
} from "@stripe/react-stripe-js";

import { selectTheme } from "../../../../reducers/appSlice";
import { useTheme } from "../../../../hooks/themeHooks";
import {
  usePaymentOptions,
  useTournament,
} from "../../../../hooks/tournamentHooks";
import { usePermissions } from "../../../../hooks/permissionHooks";
import { createNotification, FULFILLED, REJECTED } from "../../../../app/api";
import { longDelay } from "../../../../helpers/JSONapi";
import { BANK_ACCOUNT, CREDIT_CARD } from "../../../../forms/CartSections";
import { getRouting } from "../../../../helpers/Converters";
import { eventBaseRoute } from "../../../../routes/event";
import { useGolfstatusNavigate } from "../../../../hooks/navigationHooks";

const PAYMENT_FAILED = "paymentFailed";

const CreditCardDetails = (props) => {
  const { paymentType } = props;
  const dispatch = useDispatch();

  const stripe = useStripe();
  const elements = useElements();
  const mode = useSelector(selectTheme);
  const [, theme, getStyle] = useTheme();
  const tournament = useTournament();

  const { filteredNotifications } = useNotifications();

  const notifications = filteredNotifications([
    PAYMENT_FAILED,
    "updateOrderThunk",
    "internationalCardCheckThunk",
    "createBillingAddressThunk",
  ]);

  const loading = useSelector(selectLoading);
  const paymentDetails = useSelector(selectPaymentDetails);
  const {
    availableBanks,
    availableCreditCards,
    availablePaymentOptions,
  } = usePaymentOptions();

  const registrationOrder = useSelector(selectRegistrationOrder);
  const registrationCustomer = useSelector(selectRegistrationCustomer);
  const billingAddress = registrationOrder?.billingAddress ?? {};
  const permissions = usePermissions();

  const [cardDetails, setCardDetails] = useState({});
  const selectedPaymentOption = registrationOrder?.paymentOption;

  const fallback = getRouting(eventBaseRoute, [
    tournament?.publicUrl,
    "cart",
    registrationOrder.id,
    "checkout",
  ]);

  const navigate = useGolfstatusNavigate(fallback);
  // form context
  const [context, isValid, setIsValid] = useFormDataValidation({
    initialValid: true,
    setData: (update) => {
      dispatch(setPaymentDetails(update));
    },
    data: paymentDetails,
  });

  context.warningColor = addAlphaChannel(getStyle(theme.error).color, 0.7);
  context.billingAddress = billingAddress;
  context.includeFees = tournament?.organizationPaysPlatformFee;
  context.paymentDetails = paymentDetails;
  context.selectedPaymentOption = getPaymentOption(selectedPaymentOption);
  context.loading = loading;
  context.tournament = tournament;
  context.isValid = isValid;
  context.isEscrow = permissions.escrowActive();
  context.mode = mode;
  context.billingAddressComplete = () => {
    return Boolean(
      context.billingAddress?.country &&
        context.billingAddress?.street &&
        context.billingAddress?.city &&
        context.billingAddress?.state &&
        context.billingAddress?.postal
    );
  };

  context.cardValid = () => {
    return (
      cardDetails.cardFilled && cardDetails.cvvFilled && cardDetails.expFilled
    );
  };

  context.cardUpdated = (card) => {
    if (!isValid) {
      setIsValid(true);
    }
    setCardDetails(card);
  };

  context.stripePaymentUpdated = () => {
    if (!isValid) {
      setIsValid(true);
    }
  };

  context.itemStyle = getStyle(theme.surface);

  context.onBillingAddressChange = (value, field) => {
    const mapField = field?.replace?.("address", "street");
    setIsValid(true);
    let updated = { ...billingAddress };
    updated[mapField] = value;
    dispatch(setBillingAddress(updated));
  };

  context.onSelectedPaymentOptionChange = (option) => {
    setIsValid(true);
    dispatch(updateOrder({ ...registrationOrder, paymentOption: option }));
  };

  context.isUserLoggedIn = () => {
    return !!getCurrentUser();
  };

  context.getPaymentOptions = () => {
    return availablePaymentOptions(paymentType);
  };

  const displayFailedPaymentNotification = () => {
    const notification = createNotification(
      "Could not validate the card you are using at this time. Please check your card details and try again",
      faExclamationCircle,
      "warning",
      REJECTED,
      longDelay,
      [],
      PAYMENT_FAILED
    );
    dispatch(addNotification(notification));
  };

  const payWithGlobal = (paymentToken) => {
    if (!paymentToken) {
      displayFailedPaymentNotification();
      return;
    }
    const number = paymentToken?.card?.masked_card_number;
    const four =
      number?.length > 4 ? number?.substring?.(number?.length - 4) : "";
    const token = paymentToken.temporary_token;
    const exprires = `${paymentToken?.card?.expiry_month}/${paymentToken?.card?.expiry_year}`;
    const card = { brand: paymentToken?.card?.type, last4: four };
    const update = {
      ...registrationOrder,
      globalPaymentToken: token,
      stripePaymentToken: null,
      cardAttached: true,
      cardFirstName: paymentDetails.firstName,
      cardLastName: paymentDetails.lastName,
      card: card,
      expires: exprires,
      saveCard: paymentDetails.savePayment,
      registrationCustomer,
    };
    dispatch(updateOrderThunk(update));
  };

  const payWithStripe = async () => {
    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded
      return;
    }
    const card = elements.getElement(CardNumberElement);
    if (card === null) {
      const update = {
        ...registrationOrder,
        paymentOption: null,
        globalPaymentToken: null,
        stripePaymentToken: null,
        cardName: null,
        last4: null,
        brand: null,
        registrationCustomer,
      };
      dispatch(updateOrderThunk(update));
      return;
    }
    const result = await stripe.createToken(card);
    if (result.error) {
      displayFailedPaymentNotification();
      return;
    } else {
      const update = {
        ...registrationOrder,
        paymentOption: null,
        globalPaymentToken: null,
        cardAttached: true,
        stripePaymentToken: result.token?.id,
        cardName: result.token?.card?.name,
        card: result.token?.card,
        expires: `${result?.token?.card?.exp_month}/${result?.token?.card?.exp_year}`,
        saveCard: paymentDetails.savePayment,
      };
      dispatch(
        internationalCardCheckThunk({
          registrationOrder: update,
          cardInfo: {
            cardCountryCode: result.token?.card?.country,
            organizationKey: tournament?.organization?.id,
          },
        })
      );
    }
  };

  const saveBillingAddress = async () => {
    let address = {
      ...billingAddress,
      registrationOrder,
    };
    if (billingAddress.id) {
      const response = await dispatch(updateBillingAddressThunk(address));
      if(!response?.error){
        return response
      }
    } 
    return await dispatch(createBillingAddressThunk(address));
  };

  const payWithCard = (paymentToken) => {
    if (isSavedPayment()) {
      const update = { ...registrationOrder, registrationCustomer };
      update.paymentOption = selectedPaymentOption;
      update.globalPaymentToken = null;
      update.stripePaymentToken = null;
      update.status = "ready";
      dispatch(updateOrderThunk(update));
    } else {
      if (permissions.escrowActive()) {
        payWithGlobal(paymentToken);
      } else {
        payWithStripe();
      }
    }
  };

  const payWithBank = () => {
    if (isSavedPayment()) {
      const update = { ...registrationOrder };
      update.paymentOption = selectedPaymentOption;
      update.globalPaymentToken = null;
      update.stripePaymentToken = null;
      update.status = "ready";
      dispatch(updateOrderThunk(update));
    }
  };

  const clearPayment = () => {
    if (selectedPaymentOption === null) {
      const update = {
        ...registrationOrder,
        card: null,
        globalPaymentToken: null,
        cardFirstName: null,
        cardLastName: null,
        pyamentOption: null,
      };
      dispatch(updateOrderThunk(update));
    }
  };

  context.submitPayment = async (paymentToken = null) => {
    if (!isValid) {
      return;
    }

    if (context.billingAddressComplete()) {
      const response = await saveBillingAddress();
      if(response?.error){
        return;
      }
    }

    if (paymentType === CREDIT_CARD) {
      if (selectedPaymentOption === null && availableCreditCards.length > 0) {
        clearPayment();
      } else {
        payWithCard(paymentToken);
      }
    } else if (paymentType === BANK_ACCOUNT) {
      if (selectedPaymentOption === null && availableBanks?.length > 0) {
        clearPayment();
      }
      payWithBank();
    }
  };

  context.globalTokenError = (e) => {
    const notification = createNotification(
      e,
      faExclamationCircle,
      "warning",
      REJECTED,
      longDelay,
      [],
      PAYMENT_FAILED
    );
    dispatch(addNotification(notification));
  };

  // effects

  useEffect(() => {
    const user = getCurrentUser();
    if (user) {
      dispatch(getUserPaymentOptions(user.uid));
    } else {
      // TODO: call to action to invite the user to log in if they have saved payment methods
    }
  }, [dispatch]);

  const isSavedPayment = () => {
    return selectedPaymentOption?.saved ?? false;
  };

  const leftNavigation = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  useNotificationAction(
    FULFILLED,
    notifications,
    leftNavigation,
    "updateOrderThunk"
  );

  useNotificationAction(
    FULFILLED,
    notifications,
    leftNavigation,
    "internationalCardCheckThunk"
  );

  const getNavigation = () => {
    return {
      title:
        paymentType === CREDIT_CARD ? `Credit/Debit Cards` : "Bank Accounts",
      leftIcon: faChevronLeft,
      leftButtonClick: leftNavigation,
    };
  };

  const timeoutAction = () => {
    dispatch(clearNotifications());
  };

  // TODO: update these notification settings and banner to include validations
  let notificationSettings = {
    notifications: notifications,
    saveAction: context.submitPayment,
    bannerActions: getSaveBannerActions(context.submitPayment, leftNavigation),
    timeoutAction,
  };

  const [bannerNotifications] = useNotificationBanner(notificationSettings);

  const getContent = () => {
    if (paymentType === CREDIT_CARD) {
      return (
        <GSForm
          formTitle={"Credit/Debit Cards"}
          formSections={getCreditCardFormSections(context)}
        ></GSForm>
      );
    } else {
      return (
        <GSForm
          formTitle={"Bank Accounts"}
          formSections={getBankFormSections(context)}
        ></GSForm>
      );
    }
  };

  const getDrawerActions = () => {
    return [
      {
        name: "Save & Continue",
        isDisabled: !isValid,
        action: () => context.submitPayment(),
        type: "black",
      },
      {
        name: "Cancel",
        action: leftNavigation,
        type: "grey",
      },
    ];
  };

  const getDrawer = () => {
    return {
      actions: getDrawerActions(),
    };
  };

  return (
    //name the component tag
    <credit-card-details>
      <GSSidePanelPage
        header={getNavigation()}
        banner={bannerNotifications}
        content={getContent()}
        drawer={getDrawer()}
      ></GSSidePanelPage>
    </credit-card-details>
  );
};

//export the new namet
export default CreditCardDetails;
